import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Divider,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";
import {
  TrendingUp,
  Business,
  WorkOutline,
  Public,
  Event,
  CheckCircle,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { ADMIN, API_ENDPOINT } from "../../utils/constants";
import axios from "axios";
import { getHeader } from "../../utils/util";

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884d8",
  "#82ca9d",
  "#ffc658",
  "#ff7c7c",
  "#8dd1e1",
  "#d084d0",
];

const VisitorAnalyticsDashboard = ({
  searchQuery = "",
  eventCategory = "",
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    fetchAnalytics();
  }, [searchQuery, eventCategory]);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append("sq", searchQuery);
      if (eventCategory) params.append("eventCategory", eventCategory);

      const response = await axios.get(
        `${API_ENDPOINT}/user/a/get-visitor-analytics-counts?${params}`,
        getHeader(ADMIN)
      );

      const data = response.data;
      setAnalyticsData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Error loading analytics: {error}</Alert>;
  }

  if (!analyticsData) return null;

  const { totalCount, analytics, verificationSum, matchesTotalCount } =
    analyticsData;

  const StatCard = ({ title, value, icon: Icon, color }) => (
    <Card elevation={2}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" variant="body2" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="bold">
              {value.toLocaleString()}
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: `${color}20`,
              borderRadius: 2,
              p: 1.5,
            }}
          >
            <Icon sx={{ color, fontSize: 32 }} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const DataTable = ({ data, columns }) => (
    <TableContainer component={Paper} elevation={2}>
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
            {columns.map((col) => (
              <TableCell key={col.field} sx={{ fontWeight: "bold" }}>
                {col.headerName}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, idx) => (
            <TableRow key={idx} hover>
              {columns.map((col) => (
                <TableCell key={col.field}>
                  {col.field === "percentage"
                    ? `${((row.count / totalCount) * 100).toFixed(2)}%`
                    : row[col.field] || "N/A"}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderPieChart = (data, dataKey, nameKey) => (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie
          data={data.slice(0, 10)}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={({ name, percent }) =>
            `${name}: ${(percent * 100).toFixed(0)}%`
          }
          outerRadius={80}
          fill="#8884d8"
          dataKey={dataKey}
          nameKey={nameKey}
        >
          {data.slice(0, 10).map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );

  const renderBarChart = (data, dataKey, xAxisKey) => (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data.slice(0, 15)}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey={xAxisKey} angle={-45} textAnchor="end" height={100} />
        <YAxis />
        <Tooltip />
        <Legend />
        <Bar dataKey={dataKey} fill="#8884d8" />
      </BarChart>
    </ResponsiveContainer>
  );

  const renderLineChart = (data) => (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="date" angle={-45} textAnchor="end" height={100} />
        <YAxis />
        <Tooltip />
        <Legend />
        <Line
          type="monotone"
          dataKey="count"
          stroke="#8884d8"
          strokeWidth={2}
        />
      </LineChart>
    </ResponsiveContainer>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Total Visitors"
            value={totalCount}
            icon={Event}
            color="#1976d2"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Industry Sectors"
            value={analytics.industrySector.length}
            icon={Business}
            color="#2e7d32"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Departments"
            value={analytics.department.length}
            icon={WorkOutline}
            color="#ed6c02"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            title="Countries"
            value={analytics.country.length}
            icon={Public}
            color="#9c27b0"
          />
        </Grid>
      </Grid>

      {/* Verification Status */}
      <Card sx={{ mb: 4 }} elevation={2}>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Data Verification
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {Object.entries(matchesTotalCount).map(([key, matches]) => (
              <Grid item xs={12} sm={6} md={3} key={key}>
                <Chip
                  icon={matches ? <CheckCircle /> : <ErrorIcon />}
                  label={`${key}: ${matches ? "Verified" : "Mismatch"}`}
                  color={matches ? "success" : "error"}
                  variant="outlined"
                  sx={{ width: "100%" }}
                />
              </Grid>
            ))}
          </Grid>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            Verification Sums: Industry ({verificationSum.industrySector}),
            Department ({verificationSum.department}), Source (
            {verificationSum.howFoundOut}), Country ({verificationSum.country})
          </Typography>
        </CardContent>
      </Card>

      {/* Tabs for Different Analytics */}
      <Card elevation={2}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Date-wise" />
          <Tab label="Industry Sector" />
          <Tab label="Department" />
          <Tab label="How Found Out" />
          <Tab label="Country" />
          <Tab label="State" />
          <Tab label="City" />
        </Tabs>
        <Divider />
        <CardContent>
          {/* Date-wise */}
          {activeTab === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Registration Trend (Last 30 Days)
              </Typography>
              {renderLineChart(analytics.dateWise)}
              <Box sx={{ mt: 3 }}>
                <DataTable
                  data={analytics.dateWise}
                  columns={[
                    { field: "date", headerName: "Date" },
                    { field: "count", headerName: "Count" },
                    { field: "percentage", headerName: "Percentage" },
                  ]}
                />
              </Box>
            </Box>
          )}

          {/* Industry Sector */}
          {activeTab === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Visitors by Industry Sector
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  {renderPieChart(analytics.industrySector, "count", "sector")}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderBarChart(analytics.industrySector, "count", "sector")}
                </Grid>
              </Grid>
              <Box sx={{ mt: 3 }}>
                <DataTable
                  data={analytics.industrySector}
                  columns={[
                    { field: "sector", headerName: "Industry Sector" },
                    { field: "count", headerName: "Count" },
                    { field: "percentage", headerName: "Percentage" },
                  ]}
                />
              </Box>
            </Box>
          )}

          {/* Department */}
          {activeTab === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Visitors by Department
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  {renderPieChart(analytics.department, "count", "department")}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderBarChart(analytics.department, "count", "department")}
                </Grid>
              </Grid>
              <Box sx={{ mt: 3 }}>
                <DataTable
                  data={analytics.department}
                  columns={[
                    { field: "department", headerName: "Department" },
                    { field: "count", headerName: "Count" },
                    { field: "percentage", headerName: "Percentage" },
                  ]}
                />
              </Box>
            </Box>
          )}

          {/* How Found Out */}
          {activeTab === 3 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                How Visitors Found the Event
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  {renderPieChart(analytics.howFoundOut, "count", "source")}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderBarChart(analytics.howFoundOut, "count", "source")}
                </Grid>
              </Grid>
              <Box sx={{ mt: 3 }}>
                <DataTable
                  data={analytics.howFoundOut}
                  columns={[
                    { field: "source", headerName: "Source" },
                    { field: "count", headerName: "Count" },
                    { field: "percentage", headerName: "Percentage" },
                  ]}
                />
              </Box>
            </Box>
          )}

          {/* Country */}
          {activeTab === 4 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Visitors by Country
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  {renderPieChart(analytics.country, "count", "country")}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderBarChart(analytics.country, "count", "country")}
                </Grid>
              </Grid>
              <Box sx={{ mt: 3 }}>
                <DataTable
                  data={analytics.country}
                  columns={[
                    { field: "country", headerName: "Country" },
                    { field: "count", headerName: "Count" },
                    { field: "percentage", headerName: "Percentage" },
                  ]}
                />
              </Box>
            </Box>
          )}

          {/* State */}
          {activeTab === 5 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Top 10 States
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  {renderPieChart(analytics.state, "count", "state")}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderBarChart(analytics.state, "count", "state")}
                </Grid>
              </Grid>
              <Box sx={{ mt: 3 }}>
                <DataTable
                  data={analytics.state}
                  columns={[
                    { field: "state", headerName: "State" },
                    { field: "count", headerName: "Count" },
                    { field: "percentage", headerName: "Percentage" },
                  ]}
                />
              </Box>
            </Box>
          )}

          {/* City */}
          {activeTab === 6 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Top 10 Cities
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  {renderPieChart(analytics.city, "count", "city")}
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderBarChart(analytics.city, "count", "city")}
                </Grid>
              </Grid>
              <Box sx={{ mt: 3 }}>
                <DataTable
                  data={analytics.city}
                  columns={[
                    { field: "city", headerName: "City" },
                    { field: "count", headerName: "Count" },
                    { field: "percentage", headerName: "Percentage" },
                  ]}
                />
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default VisitorAnalyticsDashboard;
